import { Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import { JsPDFFontService } from '../services/js-pdf-font.service';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationService } from './configuration.service';
import { environment } from 'src/environments/environment';
import { ProductClass } from '../enums/productClass.enum';
import { ProductClassificationService } from './product-classification.service';
@Injectable({
  providedIn: 'root',
})
export class PdfGenerationService {
  modularBlue = [46, 42, 94];
  introTextColor = [136, 151, 176];
  modularBorder = [217, 225, 239];
  modularGreen = [105, 165, 162];
  numberOfPages = 1;

  private readonly spaceBetweenLinesInValue = 15;

  private readonly spaceBetweenValues = 3;

  constructor(
    private jsPDFFontService: JsPDFFontService,
    private datePipe: DatePipe,
    private translateService: TranslateService,
    private configurationService: ConfigurationService,
    private productClassificationService: ProductClassificationService
  ) {}

  async createPDF(): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'p',
      unit: 'px',
      format: 'a4',
    });
    await this.jsPDFFontService.AddFonts(doc);
    this.numberOfPages = 1;
    return doc;
  }

  text(
    doc: jsPDF,
    text: string,
    x: number,
    y: number,
    flags?: any,
    angle?: any,
    align: 'left' | 'center' | 'right' | 'justify' = 'left'
  ) {
    text = text.split(String.fromCharCode(160)).join(' ');
    doc.text(text, x, y, { angle, align });
  }

  getTextWidth(doc: jsPDF, text: string): number {
    text = text.split(String.fromCharCode(160)).join(' ');
    return doc.getTextWidth(text);
  }

  generateHeader(doc: jsPDF, imageSrc: string, demoMode = false) {
    doc.setTextColor(this.modularBlue[0], this.modularBlue[1], this.modularBlue[2]);
    doc.setFont('HindMadurai-SemiBold');
    doc.setFontSize(24);
    this.text(doc, this.translateService.instant('SUMMARY.TITLE'), 40, 70);
    doc.setTextColor(this.introTextColor[0], this.introTextColor[1], this.introTextColor[2]);
    doc.setFont('HindMadurai-Medium');
    doc.setFontSize(12);
    this.text(doc, this.translateService.instant('SUMMARY.DESCRIPTION'), 40, 90);
    doc.setDrawColor(this.modularBorder[0], this.modularBorder[1], this.modularBorder[2]);
    const isomastersLogo = new Image();
    isomastersLogo.src = demoMode ? 'assets/images/logo-coldroomconfigurator-demo.png' : 'assets/images/Variant1.png';
    doc.addImage(isomastersLogo, 'png', 280, 60, 140, 42);
    doc.line(40, 110, 400, 111);
    if (imageSrc) {
      doc.setFillColor('#A3BECF');
      doc.roundedRect(80, 160, 270, 270, 8, 8, 'F');
      doc.addImage(imageSrc, 'PNG', 90, 170, 260, 260);
    }
    const cellProductClass = this.configurationService.productClass;
    let cellTypeTitle = "STANDARD_CELL";
    if (cellProductClass !== ProductClass.STANDARD) { cellTypeTitle = cellProductClass === ProductClass.PRO ? 'PRO_CELL' : 'PRO_PLUS_CELL'; }
    const cellTitle = this.translateService.instant('PRODUCT-CLASSIFICATION.' + cellTypeTitle);
    const numWeeks = this.productClassificationService.GetDeliveryTimeInWeeks(cellProductClass);
    const cellDeliveryTimeInWeeks = this.translateService.instant('PRODUCT-CLASSIFICATION.DELIVERY_TIME') + numWeeks.toString() + " " +  
                                    this.translateService.instant('PRODUCT-CLASSIFICATION.WEEKS');
    doc.setTextColor(this.modularBlue[0], this.modularBlue[1], this.modularBlue[2]);
    doc.setFont('HindMadurai-SemiBold');
    doc.setFontSize(24);
    this.text(doc, cellTitle, 40, 480);
    doc.setTextColor(this.introTextColor[0], this.introTextColor[1], this.introTextColor[2]);
    doc.setFont('HindMadurai-Medium');
    doc.setFontSize(12);
    this.text(doc, cellDeliveryTimeInWeeks, 40, 495);
    
    if (this.configurationService.configuration.configurationId) {
      const link = environment.frontendUrl + '/?share=' + this.configurationService.configuration.configurationId;
      const introText = this.translateService.instant('SUMMARY.FINDONLINE');
      doc.setFont('HindMadurai-Medium');
      doc.setFontSize(12);
      doc.setTextColor(this.introTextColor[0], this.introTextColor[1], this.introTextColor[2]);
      this.text(doc, introText, 40, 530);
      doc.setTextColor(this.modularBlue[0], this.modularBlue[1], this.modularBlue[2]);
      doc.textWithLink(link, 40, 544, { url: link });
    }

    doc.addPage('a4', 'p');
    this.numberOfPages++;
  }

  generateAdditionalImages(doc: jsPDF, imageInside: string, imagesWithDoors: object, height: number) {
    let right = false;
    const rowHeight = 195;
    if (height + rowHeight > 600) {
      doc.addPage('a4', 'p');
      this.numberOfPages++;
      height = 15;
    }
    if (imageInside) {
      ({ height, right } = this.addImage(doc, height, imageInside, right, this.translateService.instant('INSIDEVIEW')));
    }
    const doorSides = Object.keys(imagesWithDoors).sort((a, b) => (+a < 2 ? +a + 4 : +a) - (+b < 2 ? +b + 4 : +b));
    for (const side of doorSides) {
      if (height + rowHeight > 600) {
        doc.addPage('a4', 'p');
        this.numberOfPages++;
        height = 15;
      }
      ({ height, right } = this.addImage(
        doc,
        height,
        imagesWithDoors[side],
        right,
        this.translateService.instant('WALL.' + side)
      ));
    }
  }

  private addImage(doc: jsPDF, height: number, image: string, right: boolean, text: string) {
    doc.setFillColor('#A3BECF');
    doc.roundedRect(right ? 240 : 40, height, 175, 175, 8, 8, 'F');
    doc.addImage(image, 'PNG', right ? 250 : 50, height + 5, 160, 160);
    const width = this.getTextWidth(doc, text) / 2;
    doc.text(text, (right ? 327.5 : 127.5) - width, height + 185);
    if (right === false) {
      right = true;
    } else {
      height += 200;
      right = false;
    }
    return { height, right };
  }

  generateStep(
    doc: jsPDF,
    stepNumber: number,
    stepContent: StepItem[],
    height: number,
    stepTitle?: string,
    conflicts = false
  ): number {
    if (height + 20 + this.getBoxHeight(stepContent) > 600) {
      doc.addPage('a4', 'p');
      this.numberOfPages++;
      height = 15;
    }
    if (stepNumber) {
      this.generateStepHeader(doc, stepNumber, height);
    } else {
      if (stepTitle) {
        this.generateStepTitle(doc, stepTitle, height);
      }
    }
    if (!conflicts) {
      return this.generateStepItems(doc, stepContent, height + 20);
    } else {
      return this.generateConflictItems(doc, stepContent, height + 20);
    }
  }

  generateStepHeader(doc: jsPDF, stepNumber: number, startingHeight: number) {
    doc.setTextColor(this.modularGreen[0], this.modularGreen[1], this.modularGreen[2]);
    doc.setFont('HindMadurai-SemiBold');
    doc.setFontSize(18);
    this.text(doc, this.stepToDisplayNumber(stepNumber), 40, startingHeight + 12);
    doc.setTextColor(this.modularBlue[0], this.modularBlue[1], this.modularBlue[2]);
    doc.setFontSize(14);
    this.text(doc, this.translateService.instant('STEP' + stepNumber + '.SUMMARYTITLE'), 70, startingHeight + 12);
  }

  generateStepTitle(doc: jsPDF, title: string, startingHeight: number) {
    doc.setFont('HindMadurai-SemiBold');
    doc.setTextColor(this.modularBlue[0], this.modularBlue[1], this.modularBlue[2]);
    doc.setFontSize(14);
    this.text(doc, this.translateService.instant(title), 40, startingHeight + 12);
  }

  generateStepItems(doc: jsPDF, stepContent: StepItem[], startingHeight: number) {
    const boxHeight = this.getBoxHeight(stepContent);
    doc.setFillColor(250, 251, 253);
    doc.roundedRect(40, startingHeight, 360, boxHeight, 8, 8, 'F');

    doc.setFont('HindMadurai-Medium');
    doc.setFontSize(12);
    doc.setTextColor(this.introTextColor[0], this.introTextColor[1], this.introTextColor[2]);
    let height = startingHeight + 5;
    stepContent.forEach(step => {
      step.title.split('\n').forEach(line => {
        this.text(doc, this.translateService.instant(line), 50, height + 11);
        height += this.spaceBetweenLinesInValue;
      });
      const extraLinesInValue = step.value.split('\n').length - step.title.split('\n').length;
      if (extraLinesInValue > 0) {
        height += this.spaceBetweenLinesInValue * extraLinesInValue;
      }
      height += this.spaceBetweenValues;
    });

    doc.setFont('HindMadurai-Medium');
    doc.setFontSize(14);
    doc.setTextColor(this.modularBlue[0], this.modularBlue[1], this.modularBlue[2]);
    height = startingHeight + 5;
    stepContent.forEach(step => {
      if (step.value != null) {
        step.value.split('\n').forEach(line => {
          this.text(doc, line, 390, height + 11, {}, 0, 'right');
          height += this.spaceBetweenLinesInValue;
        });
      }
      height += this.spaceBetweenValues;
    });

    return startingHeight + boxHeight;
  }

  generateConflictItems(doc: jsPDF, stepContent: StepItem[], startingHeight: number) {
    const boxHeight = this.getBoxHeightAllOnleft(stepContent);
    doc.setFillColor(255, 245, 245);
    doc.roundedRect(40, startingHeight, 360, boxHeight, 8, 8, 'F');

    doc.setTextColor(this.modularBlue[0], this.modularBlue[1], this.modularBlue[2]);
    let height = startingHeight + 5;
    stepContent.forEach(step => {
      doc.setFontSize(12);
      step.title.split('\n').forEach(line => {
        this.text(doc, line, 50, height + 11);
        height += this.spaceBetweenLinesInValue;
      });
      doc.setFontSize(10);
      if (step.value != null) {
        step.value.split('\n').forEach(line => {
          this.text(doc, line, 50, height + 11, {}, 0, 'left');
          height += this.spaceBetweenLinesInValue;
        });
      }
      height += this.spaceBetweenValues;
    });
    doc.setFontSize(12);
    return startingHeight + boxHeight;
  }

  getBoxHeight(stepContent: StepItem[]) {
    const numberOfLines = stepContent
      .map(s => (s.value == null ? 1 : s.value.split('\n').length))
      .reduce((a, b) => a + b, 0);
    return stepContent.length * this.spaceBetweenValues + numberOfLines * this.spaceBetweenLinesInValue + 10;
  }
  getBoxHeightAllOnleft(stepContent: StepItem[]) {
    const numberOfLines = stepContent
      .map(s => (s.value == null ? 0 : s.value.split('\n').length) + s.title.split('\n').length)
      .reduce((a, b) => a + b, 0);
    return stepContent.length * this.spaceBetweenValues + numberOfLines * this.spaceBetweenLinesInValue + 10;
  }

  stepToDisplayNumber(step: number): string {
    if (step >= 10) {
      return step.toString();
    }
    return '0' + step;
  }

  generateFooter(doc: jsPDF) {
    for (let page = 1; page <= this.numberOfPages; page++) {
      doc.setPage(page);
      doc.setTextColor(this.introTextColor[0], this.introTextColor[1], this.introTextColor[2]);
      doc.setFont('HindMadurai-Medium');
      doc.setFontSize(10);
      if (this.configurationService.configuration.configurationId) {
        this.text(
          doc,
          this.translateService.instant('SUMMARY.YOUR_UNIQUE_CONFIGURATION_ID') +
            ': ' +
            this.configurationService.configuration.configurationId,
          40,
          600
        );
      }
      const text =
        this.translateService.instant('SUMMARY.GENERATEDON') +
        ':  ' +
        this.datePipe.transform(new Date(), 'yyyy-MM-dd HH:mm:ss');
      this.text(doc, text, 40, 610);

      doc.setTextColor(this.modularBlue[0], this.modularBlue[1], this.modularBlue[2]);
      doc.setFontSize(16);
      const pageNumberText = page + '/' + this.numberOfPages;
      this.text(doc, pageNumberText, 380, 600, {}, 0, 'right');
    }
  }
}

export class StepItem {
  title: string;
  value: string | null;
}
